// Shared race types. The contract between signal, race, world, director and hud.
// Defined in .taiga/knowledge/code/index.md; changing anything here must be called out in a handoff.

export type Phase = 'starting' | 'running' | 'finished';
export type CatId = 'nemo' | 'sirius' | 'tyyne' | 'silkki' | 'misse' | 'growl'; // lane 0..5

/** The cats in lane order: cat i always runs in lane i. */
export const CAT_IDS: readonly CatId[] = ['nemo', 'sirius', 'tyyne', 'silkki', 'misse', 'growl'];

export interface SignalFrame {
  state: Phase;
  cats?: { pos: number; ham: boolean; nip: boolean }[]; // running: pos 0..1 (raw/1023)
  ham?: { lane: number; pos: number } | null;
  catnip?: { lane: number; pos: number } | null;
  times?: { seconds: number; dsq: boolean }[]; // finished, counted from post time
}

export interface StampedFrame extends SignalFrame {
  t: number; // signal time, ms
  raceNo: number;
}

export interface RaceSource {
  readonly kind: 'live' | 'replay' | 'sim';
  readonly label: string; // shown in the HUD, e.g. "LIVE · kissaderby.fi"
  start(
    onFrame: (f: SignalFrame, arrivalMs: number) => void,
    onStatus: (s: 'connecting' | 'open' | 'reconnecting' | 'failed') => void,
  ): void;
  stop(): void;
}

export interface CatSnapshot {
  id: CatId;
  lane: number;
  signalPos: number; // truth, 0..1: standings, gaps, race map
  displayPos: number; // smoothed, 0..1: 3D placement (ignored while tripping)
  speed: number; // m/s along the track, from displayPos
  eating: boolean;
  tripping: boolean;
  finished: boolean;
  finishPlace?: number; // order of crossing
  place: number; // 1..6, current
  gapToLeader: number; // share of the track
}

export interface RaceSnapshot {
  t: number; // render time (signal clock, ms)
  source: 'live' | 'replay' | 'sim';
  raceNo: number;
  phase: Phase;
  phaseElapsedMs: number;
  joinedMidRace: boolean;
  cats: CatSnapshot[]; // in lane order
  ham: { lane: number; pos: number } | null;
  catnip: { lane: number; pos: number } | null;
  results?: { id: CatId; seconds: number; dsq: boolean; place: number }[]; // only in 'finished'
  signal: 'ok' | 'thin' | 'lost'; // buffer health
}

export type RaceEvent =
  | { type: 'phase'; t: number; phase: Phase; raceNo: number }
  | { type: 'ham-start' | 'ham-end'; t: number; cat: CatId }
  | { type: 'ham-moved'; t: number; lane: number; pos: number }
  | { type: 'catnip-catch'; t: number; cat: CatId; at: number }
  | { type: 'catnip-landing'; t: number; cat: CatId; from: number; to: number; delta: number; duringGap: boolean }
  | { type: 'lead-change'; t: number; leader: CatId; previous: CatId }
  | { type: 'finish'; t: number; cat: CatId; place: number }
  | { type: 'close-finish'; t: number; cats: [CatId, CatId]; marginMs: number }
  | { type: 'results'; t: number; results: NonNullable<RaceSnapshot['results']> }
  | { type: 'signal'; t: number; status: 'ok' | 'thin' | 'lost' | 'source-changed' };

export interface RaceTimeline {
  push(frame: SignalFrame, arrivalMs: number): void;
  sample(renderT: number): RaceSnapshot;
  eventsBetween(t0: number, t1: number): RaceEvent[]; // fired by the loop each frame
  peek(renderT: number, horizonMs: number): RaceEvent[]; // look-ahead, ≤ 2 s
  newestT(): number;
}
